/**
 * Cloudflare Turnstile Widget Component
 *
 * Implements bot protection for anonymous trial users.
 * - Web: Direct DOM rendering via useTurnstileWebRenderer hook
 * - Native: WebView-based rendering with message passing
 *
 * Phase: 1.6 - Anonymous Trial Strategy
 */

import { forwardRef, useEffect, useState, useRef, useImperativeHandle } from 'react';
import { View, Platform, ActivityIndicator, Text } from 'react-native';
import WebView from 'react-native-webview';
import { useEnv } from '../hooks/useEnv';
import { useTurnstileEnabledFlag } from '../hooks/useFeatureFlags';
import { useTurnstileWebRenderer } from '../hooks/useTurnstileWebRenderer';
import {
  parseTurnstileMessage,
  ALLOWED_ORIGINS,
  type TurnstileWidgetProps,
  type TurnstileHandle,
} from '../lib/turnstile/turnstileConfig';
import { generateTurnstileHtml } from '../lib/turnstile/turnstileHtml';

export type { TurnstileWidgetProps, TurnstileHandle };

/**
 * TurnstileWidget
 *
 * Renders a Cloudflare Turnstile challenge. Supports all widget modes:
 * - Managed: Visible checkbox (appearance='always', size='normal')
 * - Non-Interactive: Brief visible spinner
 * - Invisible: No UI, auto-executes (size/appearance ignored by SDK)
 *
 * The mode is determined by the site key configuration in the Cloudflare dashboard,
 * not by these client-side parameters. Defaults are safe for all modes.
 *
 * @see https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 */
export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(function TurnstileWidget({
  onSuccess,
  onError,
  onExpire,
  onTimeout,
  theme = 'auto',
  size = 'flexible',
  appearance = 'interaction-only',
  execution = 'render',
  refreshExpired = 'auto',
  retry = 'auto',
  language,
  action = 'analyze_message',
  cData,
}, ref) {
  const { EXPO_PUBLIC_TURNSTILE_SITE_KEY, EXPO_PUBLIC_E2E_MODE } = useEnv();
  const turnstileEnabled = useTurnstileEnabledFlag();

  // E2E BYPASS: Auto-complete with mock token for E2E testing
  const isE2EMode = (() => {
    if (EXPO_PUBLIC_E2E_MODE === 'true') return true;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('e2e') === 'true';
    }
    return false;
  })();

  // Shared refs (used by both web hook and native WebView)
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const webViewRef = useRef<WebView>(null);
  const tokenReceivedRef = useRef(false);

  // Native-only state (web state is managed by useTurnstileWebRenderer)
  const [nativeLoading, setNativeLoading] = useState(true);
  const [nativeError, setNativeError] = useState<string | null>(null);

  // Web rendering (script loading, widget rendering, cleanup)
  const { loading: webLoading, error: webError } = useTurnstileWebRenderer({
    siteKey: EXPO_PUBLIC_TURNSTILE_SITE_KEY || '',
    containerRef,
    widgetIdRef,
    tokenReceivedRef,
    onSuccess,
    onError,
    onExpire,
    onTimeout,
    config: { theme, size, appearance, execution, refreshExpired, retry, language, action, cData },
  });

  // E2E mode: auto-deliver mock token
  useEffect(() => {
    if (isE2EMode) {
      console.log('[TurnstileWidget] E2E mode detected - auto-completing with mock token');
      const timer = setTimeout(() => {
        onSuccess('e2e-mock-turnstile-token');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isE2EMode, onSuccess]);

  // Phase 2: Expose reset() so parent can trigger silent token regeneration
  useImperativeHandle(ref, () => ({
    reset: () => {
      console.log('[TurnstileWidget] reset() called');
      tokenReceivedRef.current = false;

      if (isE2EMode) {
        setTimeout(() => onSuccess('e2e-mock-turnstile-token'), 50);
        return;
      }

      if (Platform.OS === 'web') {
        const turnstile = (window as any).turnstile;
        if (turnstile && widgetIdRef.current) {
          try {
            turnstile.reset(widgetIdRef.current);
            console.log('[TurnstileWidget] Widget reset on web');
          } catch (err) {
            console.warn('[TurnstileWidget] Failed to reset:', err);
          }
        }
      } else {
        webViewRef.current?.injectJavaScript(`
          if (typeof turnstile !== 'undefined' && window.__turnstileWidgetId) {
            turnstile.reset(window.__turnstileWidgetId);
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'reset_ack' }));
          }
          true;
        `);
      }
    },
  }), [isE2EMode, onSuccess]);

  // Feature flag kill switch — hide widget when Turnstile is disabled
  // IMPORTANT: Must be placed after all hooks to satisfy Rules of Hooks
  if (!turnstileEnabled) {
    return null;
  }

  // Skip rendering in E2E mode
  if (isE2EMode) {
    return null;
  }

  // Resolve loading/error from the correct platform source
  const loading = Platform.OS === 'web' ? webLoading : nativeLoading;
  const error = Platform.OS === 'web' ? webError : nativeError;

  // Handle messages from WebView (native only)
  const handleMessage = (event: { nativeEvent: { data: string } }) => {
    try {
      const message = parseTurnstileMessage(event.nativeEvent.data);

      switch (message.type) {
        case 'success':
          console.log('[TurnstileWidget] Token received');
          tokenReceivedRef.current = true;
          setNativeLoading(false);
          if (message.token) {
            onSuccess(message.token);
          }
          break;

        case 'error':
          if (tokenReceivedRef.current) {
            console.warn('[TurnstileWidget] Ignoring post-success error:', message.error);
            break;
          }
          console.error('[TurnstileWidget] Challenge failed:', message.error);
          setNativeLoading(false);
          setNativeError(message.error || 'Unknown error');
          onError?.(message.error || 'Unknown error');
          break;

        case 'expire':
          console.log('[TurnstileWidget] Token expired');
          tokenReceivedRef.current = false;
          setNativeLoading(true);
          onExpire?.();
          break;

        case 'timeout':
          if (tokenReceivedRef.current) {
            console.warn('[TurnstileWidget] Ignoring post-success timeout');
            break;
          }
          console.log('[TurnstileWidget] Interactive challenge timeout');
          setNativeLoading(false);
          onTimeout?.();
          break;

        case 'ready':
          console.log('[TurnstileWidget] Widget loaded');
          setNativeLoading(false);
          break;

        case 'reset_ack':
          console.log('[TurnstileWidget] Widget reset acknowledged');
          break;

        default:
          console.warn('[TurnstileWidget] Unknown message type:', message.type);
      }
    } catch (err) {
      console.error('[TurnstileWidget] Failed to parse message:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to parse message';
      setNativeError(errorMsg);
      onError?.(errorMsg);
    }
  };

  // Error state
  if (error) {
    return (
      <View className="p-4 bg-red-100 rounded-lg">
        <Text className="text-red-800 text-sm">
          Bot protection failed: {error}
        </Text>
      </View>
    );
  }

  // Web platform: Render Turnstile directly on parent page (mobile-compatible)
  if (Platform.OS === 'web') {
    return (
      <View className="w-full flex items-center justify-center">
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      </View>
    );
  }

  // Generate HTML content for WebView (native platforms)
  const htmlContent = generateTurnstileHtml(EXPO_PUBLIC_TURNSTILE_SITE_KEY || '', {
    theme,
    size,
    appearance,
    execution,
    refreshExpired,
    retry,
    language,
    action,
    cData,
  });

  // Native platforms: Use WebView
  return (
    <View className="w-full flex items-center justify-center">
      <View className="w-[300px] relative" style={{ minHeight: 1 }}>
        <WebView
          ref={webViewRef}
          originWhitelist={['*']}
          source={{ html: htmlContent }}
          onMessage={handleMessage}
          javaScriptEnabled={true}
          domStorageEnabled={true}
          scalesPageToFit={true}
          scrollEnabled={false}
          style={{
            backgroundColor: 'transparent',
            width: '100%',
            height: '100%',
          }}
          onShouldStartLoadWithRequest={(request) => {
            return ALLOWED_ORIGINS.some((origin) => request.url.startsWith(origin));
          }}
        />
        {loading && (
          <View className="absolute inset-0 flex items-center justify-center bg-white/50">
            <ActivityIndicator size="small" />
            <Text className="text-xs text-gray-600 mt-2">Verifying...</Text>
          </View>
        )}
      </View>
    </View>
  );
});
